import React, { useState, useEffect, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import MaterialTable from "@material-table/core";
import OpacityIcon from '@mui/icons-material/Opacity';
import './Caudalimetro.css';

const SSE_URL_289 = "/sensor289/events";
const SSE_URL_291 = "/sensor291/events";

const STORAGE_KEY = 'caudalimetro_historico';

function loadHistorico() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    return parsed;
  } catch { return {}; }
}

function saveHistorico(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function Gauge({ value, max, label, unit, color, decimals = 1, id }) {
  const v = value || 0;
  const angle = Math.min((v / max) * 240 - 120, 120);
  const percentage = Math.min((v / max) * 100, 100);

  return (
    <div className="gauge-container">
      <svg viewBox="0 0 200 160" className="gauge-svg">
        <defs>
          <linearGradient id={`grad-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: '#e0f7fa', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: color, stopOpacity: 1 }} />
          </linearGradient>
        </defs>
        <path
          d="M 20 140 A 80 80 0 1 1 180 140"
          fill="none"
          stroke="#e0e0e0"
          strokeWidth="14"
          strokeLinecap="round"
        />
        <path
          d="M 20 140 A 80 80 0 1 1 180 140"
          fill="none"
          stroke={`url(#grad-${id})`}
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={`${percentage * 2.95} 999`}
          className="gauge-fill"
        />
        {[...Array(13)].map((_, i) => {
          const a = (-120 + i * 20) * (Math.PI / 180);
          const x1 = 100 + 72 * Math.cos(a);
          const y1 = 100 + 72 * Math.sin(a);
          const x2 = 100 + 80 * Math.cos(a);
          const y2 = 100 + 80 * Math.sin(a);
          return (
            <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#90a4ae" strokeWidth="1.5" />
          );
        })}
        <g className="gauge-needle" style={{ transform: `rotate(${angle}deg)`, transformOrigin: '100px 100px' }}>
          <line x1="100" y1="100" x2="100" y2="30" stroke="#37474f" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="100" cy="100" r="5" fill="#37474f" />
        </g>
        <circle cx="100" cy="100" r="3" fill={color} />
      </svg>
      <div className="gauge-value" style={{ color }}>
        {(v).toFixed(decimals)}
      </div>
      <div className="gauge-unit">{unit}</div>
      <div className="gauge-label">{label}</div>
    </div>
  );
}

function WaterDrop({ active }) {
  return (
    <div className={`water-drops ${active ? 'active' : ''}`}>
      <div className="drop drop-1">💧</div>
      <div className="drop drop-2">💧</div>
      <div className="drop drop-3">💧</div>
    </div>
  );
}

function SensorCard({ name, color, bgGradient, borderColor, caudal, consumo, connected }) {
  const flowActive = caudal > 0.5;
  return (
    <Paper elevation={2} sx={{
      p: 2,
      borderRadius: 3,
      background: bgGradient,
      border: `1px solid ${borderColor}`,
      textAlign: 'center',
    }}>
      <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
        <OpacityIcon sx={{ color, mr: 0.5, fontSize: 22 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color }}>
          Sensor {name}
        </Typography>
        <Chip
          label={connected ? 'Online' : 'Offline'}
          size="small"
          sx={{
            ml: 1,
            bgcolor: connected ? '#e8f5e9' : '#ffebee',
            color: connected ? '#2e7d32' : '#c62828',
            fontWeight: 'bold',
            fontSize: '0.7rem',
          }}
        />
      </Box>

      <WaterDrop active={flowActive} />

      <Gauge
        value={caudal}
        max={500}
        label="Caudal instantáneo"
        unit="L/min"
        color={color}
        decimals={2}
        id={`caudal-${name}`}
      />

      <Paper elevation={1} sx={{
        mt: 2, p: 1.5, borderRadius: 2,
        background: 'rgba(255,255,255,0.7)',
      }}>
        <Typography variant="caption" sx={{ color: '#546e7a' }}>
          Consumo total acumulado
        </Typography>
        <Typography variant="h4" sx={{
          color,
          fontWeight: 'bold',
          fontFamily: '"Roboto Mono", monospace',
          lineHeight: 1.2,
        }}>
          {(consumo || 0).toFixed(1)} <span style={{ fontSize: '0.45em' }}>L</span>
        </Typography>
      </Paper>
    </Paper>
  );
}

export default function Caudalimetro() {
  const [s289, setS289] = useState({ caudal: 0, consumo: 0, connected: false });
  const [s291, setS291] = useState({ caudal: 0, consumo: 0, connected: false });
  const [historico, setHistorico] = useState([]);
  const historicoRef = useRef(loadHistorico());

  const buildRows = useCallback((h) => {
    const rows = [];
    Object.entries(h).forEach(([date, sensors]) => {
      const s289 = sensors['289'];
      const s291 = sensors['291'];
      const row = {
        fecha: date.split('-').reverse().join('/'),
        fechaRaw: date,
        consumo289: (s289 && s289.lastValue != null && s289.firstValue != null) ? (s289.lastValue - s289.firstValue).toFixed(1) : '-',
        total289: (s289 && s289.lastValue != null) ? s289.lastValue.toFixed(1) : '-',
        consumo291: (s291 && s291.lastValue != null && s291.firstValue != null) ? (s291.lastValue - s291.firstValue).toFixed(1) : '-',
        total291: (s291 && s291.lastValue != null) ? s291.lastValue.toFixed(1) : '-',
      };
      rows.push(row);
    });
    rows.sort((a, b) => b.fechaRaw.localeCompare(a.fechaRaw));
    return rows;
  }, []);

  const updateHistorico = useCallback((sensorKey, value) => {
    if (value == null || isNaN(value)) return;
    const now = new Date();
    const dateKey = now.toISOString().slice(0, 10);
    const h = historicoRef.current;

    if (!h[dateKey]) h[dateKey] = {};
    if (!h[dateKey][sensorKey]) {
      h[dateKey][sensorKey] = { firstValue: value, lastValue: value };
    } else {
      h[dateKey][sensorKey].lastValue = value;
    }
    historicoRef.current = h;
    saveHistorico(h);
    setHistorico(buildRows(h));
  }, [buildRows]);

  // SSE sensor 289
  useEffect(() => {
    const es = new EventSource(SSE_URL_289);
    es.addEventListener('state', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.id === 'sensor-caudal_instantaneo') {
          setS289(prev => ({ ...prev, caudal: data.value, connected: true }));
        }
        if (data.id === 'sensor-consumo_total') {
          setS289(prev => ({ ...prev, consumo: data.value, connected: true }));
          updateHistorico('289', data.value);
        }
      } catch {}
    });
    es.onerror = () => setS289(prev => ({ ...prev, connected: false }));
    return () => es.close();
  }, [updateHistorico]);

  // SSE sensor 291
  useEffect(() => {
    const es = new EventSource(SSE_URL_291);
    es.addEventListener('state', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.id === 'sensor-caudal_instantaneo') {
          setS291(prev => ({ ...prev, caudal: data.value, connected: true }));
        }
        if (data.id === 'sensor-consumo_total') {
          setS291(prev => ({ ...prev, consumo: data.value, connected: true }));
          updateHistorico('291', data.value);
        }
      } catch {}
    });
    es.onerror = () => setS291(prev => ({ ...prev, connected: false }));
    return () => es.close();
  }, [updateHistorico]);

  // Load historico from localStorage on mount
  useEffect(() => {
    setHistorico(buildRows(historicoRef.current));
  }, [buildRows]);

  const totalConsumo = (s289.consumo || 0) + (s291.consumo || 0);

  return (
    <Paper elevation={3} className="caudalimetro-paper">
      <Box p={2}>
        <Box display="flex" alignItems="center" mb={2}>
          <OpacityIcon sx={{ color: '#0288d1', mr: 1, fontSize: 28 }} />
          <Typography variant="h6" component="div" sx={{ color: '#01579b', fontWeight: 'bold' }}>
            Caudalímetros
          </Typography>
        </Box>

        {/* Total card arriba */}
        <Paper elevation={2} sx={{
          p: 2, mb: 2, borderRadius: 3,
          background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
          border: '1px solid #a5d6a7',
          textAlign: 'center',
        }}>
          <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500 }}>
            Consumo Total (289 + 291)
          </Typography>
          <Typography variant="h3" sx={{
            color: '#1b5e20',
            fontWeight: 'bold',
            fontFamily: '"Roboto Mono", monospace',
            lineHeight: 1.3,
          }}>
            {totalConsumo.toFixed(1)} <span style={{ fontSize: '0.4em' }}>L</span>
          </Typography>
        </Paper>

        {/* Sensor cards: 289 primero, 291 segundo */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <SensorCard
              name="289"
              color="#e65100"
              bgGradient="linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)"
              borderColor="#ffcc80"
              caudal={s289.caudal}
              consumo={s289.consumo}
              connected={s289.connected}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SensorCard
              name="291"
              color="#0288d1"
              bgGradient="linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)"
              borderColor="#90caf9"
              caudal={s291.caudal}
              consumo={s291.consumo}
              connected={s291.connected}
            />
          </Grid>
        </Grid>

        {/* Tabla historial combinada */}
        <Box mt={3}>
          <MaterialTable
            title="Historial de consumo diario"
            data={historico}
            columns={[
              { title: "Fecha", field: "fecha" },
              { title: "Consumo 289 (L)", field: "consumo289" },
              { title: "Total 289 (L)", field: "total289" },
              { title: "Consumo 291 (L)", field: "consumo291" },
              { title: "Total 291 (L)", field: "total291" },
            ]}
            options={{
              pageSize: 10,
              pageSizeOptions: [10, 25, 50],
              actionsColumnIndex: -1,
              emptyRowsWhenPaging: false,
              headerStyle: {
                backgroundColor: '#e0f7fa',
                color: '#01579b',
                fontWeight: 'bold',
              },
            }}
            localization={{
              pagination: {
                labelDisplayedRows: '{from}-{to} de {count}',
                labelRowsSelect: 'filas',
              },
              toolbar: {
                searchPlaceholder: 'Buscar...',
                searchTooltip: 'Buscar',
              },
              header: { actions: '' },
              body: {
                emptyDataSourceMessage: 'Esperando datos de los sensores...',
              },
            }}
          />
        </Box>
      </Box>
    </Paper>
  );
}
